import { Flex, Box, Text } from '@chakra-ui/react';
import DetailCard from '../shared/DetailCard';
import DetailRow from '../shared/DetailRow';
import { formatDate } from '../../../../utils/formatDate';

interface SignerInfo {
  accountId: string;
  name?: string;
}

interface ThresholdUpdateDetailsProps {
  newThreshold: number;
  signersCount: number;
  expiresAt?: number;
  waitForExpiry?: boolean;
  isMultisig: boolean;
  addedSigners?: SignerInfo[];
  removedSigners?: SignerInfo[];
  thresholdChanged?: boolean;
  oldThreshold?: number;
  createdBy?: { name: string; accountId: string };
}

const ThresholdUpdateDetails = ({
  newThreshold,
  signersCount,
  expiresAt,
  waitForExpiry,
  isMultisig,
  addedSigners,
  removedSigners,
  thresholdChanged,
  oldThreshold,
  createdBy,
}: ThresholdUpdateDetailsProps) => (
  <DetailCard>
    <DetailRow label="New Threshold" value={`${newThreshold} of ${signersCount} signers`} />

    {thresholdChanged && oldThreshold != null && (
      <DetailRow
        label="Threshold Change"
        value={`${oldThreshold} → ${newThreshold}`}
      />
    )}

    {addedSigners && addedSigners.length > 0 && (
      <DetailRow
        label={addedSigners.length === 1 ? 'Added Signer' : `Added Signers (${addedSigners.length})`}
        value={
          <Flex direction="column" gap={0.5} alignItems="flex-end">
            {addedSigners.map((s) => (
              <Text key={s.accountId} size="sm" color="green.600">
                {s.name ? `${s.name} (${s.accountId})` : s.accountId}
              </Text>
            ))}
          </Flex>
        }
      />
    )}

    {removedSigners && removedSigners.length > 0 && (
      <DetailRow
        label={removedSigners.length === 1 ? 'Removed Signer' : `Removed Signers (${removedSigners.length})`}
        value={
          <Flex direction="column" gap={0.5} alignItems="flex-end">
            {removedSigners.map((s) => (
              <Text key={s.accountId} size="sm" color="red.500">
                {s.name ? `${s.name} (${s.accountId})` : s.accountId}
              </Text>
            ))}
          </Flex>
        }
      />
    )}

    {createdBy && (
      <DetailRow
        label="Created by"
        value={createdBy.name ? `${createdBy.name} (${createdBy.accountId})` : createdBy.accountId}
      />
    )}
    {isMultisig && expiresAt && (
      <DetailRow
        label="Expires"
        value={
          <Flex alignItems="center" gap={2}>
            <Box px={2} py={0.5} bg="blackAlpha.50" borderRadius={4}>
              <Text size="xs" color="blackAlpha.700" whiteSpace="nowrap">
                {formatDate(expiresAt)}
              </Text>
            </Box>
            {waitForExpiry && (
              <Box px={1.5} py={0.5} bg="orange.100" borderRadius={4}>
                <Text size="xs" color="orange.700" fontWeight="600" fontSize="10px" whiteSpace="nowrap">
                  Wait for Expiry
                </Text>
              </Box>
            )}
          </Flex>
        }
      />
    )}
  </DetailCard>
);

export default ThresholdUpdateDetails;
